const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const API_PREFIX = API_BASE_URL ? `${API_BASE_URL}/api` : '/api';
const API_ORIGIN = API_BASE_URL;

const getErrorMessage = (detail, status) => {
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        const labels = {
            full_name: 'ФИО', phone_number: 'Телефон', email: 'Email',
            delivery_address: 'Адрес пункта СДЭК', telegram: 'Telegram', promocode: 'Промокод',
        };
        const fields = [...new Set(detail.map(item => labels[item.loc?.[item.loc.length - 1]]).filter(Boolean))];
        return fields.length ? `Проверьте поля: ${fields.join(', ')}.` : 'Проверьте данные заказа и попробуйте ещё раз.';
    }
    return `Ошибка ${status}`;
};

export const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const cleanPath = path.replace(/\\/g, '/');
    const normalizedPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
    return `${API_ORIGIN}${normalizedPath}`;
};

const publicFetch = async (endpoint, options = {}) => {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const url = `${API_PREFIX}${cleanEndpoint}`;
    const controller = new AbortController();
    let timedOut = false;
    const timeout = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
    }, 20000);
    const abortFromCaller = () => controller.abort();
    if (options.signal?.aborted) controller.abort();
    options.signal?.addEventListener('abort', abortFromCaller, { once: true });

    const mergedOptions = {
        headers: {
            'Content-Type': 'application/json',
            ...(options.headers || {}),
        },
        ...options,
        signal: controller.signal,
    };

    if (mergedOptions.body instanceof FormData) {
        delete mergedOptions.headers['Content-Type'];
    }

    try {
        const response = await fetch(url, mergedOptions);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const error = new Error(getErrorMessage(errorData.detail, response.status));
            error.status = response.status;
            throw error;
        }

        if (response.status === 204) return null;

        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) return null;

        return await response.json();
    } catch (error) {
        if (timedOut) throw new Error('request_timeout');
        console.error('API Error:', error);
        throw error;
    } finally {
        window.clearTimeout(timeout);
        options.signal?.removeEventListener('abort', abortFromCaller);
    }
};

export const publicApi = {
    catalog: {
        getAll: async (options = {}) => {
            const endpoint = options.includePreorder ? 'catalog/?include_preorder=true' : 'catalog/';
            const data = await publicFetch(endpoint, { cache: 'no-store', signal: options.signal });
            return data.map((product) => ({
                ...product,
                images: (product.images || []).map((img) => getImageUrl(img)),
            }));
        },
    },
    products: {
        getAll: async (params = {}) => {
            const queryString = new URLSearchParams(params).toString();
            const endpoint = queryString ? `products?${queryString}` : 'products';
            const data = await publicFetch(endpoint);

            return data.map((product) => ({
                ...product,
                images: (product.images || []).map((img) => getImageUrl(img)),
            }));
        },
    },

    itemInstances: {
        getByProduct: (productId) => publicFetch(`instances/?product_id=${productId}`),
    },

    promo_codes: {
        check: (code) => publicFetch('promocodes/check', {
            method: 'POST',
            body: JSON.stringify({ code }),
        }),
    },

    purchases: {
        getPublic: (id, token) => publicFetch(`purchases/${id}/public?token=${token}`),
    },

    checkout: {
        create: (data) => publicFetch('checkout', {
            method: 'POST',
            body: JSON.stringify(data),
        }),
    },

    delivery: {
        capabilities: () => publicFetch('cdek/capabilities'),
        cities: query => publicFetch(`cdek/cities?${new URLSearchParams({ query })}`),
        points: cityCode => publicFetch(`cdek/delivery-points?${new URLSearchParams({ city_code: cityCode })}`),
        quote: data => publicFetch('cdek/quotes', { method: 'POST', body: JSON.stringify(data) }),
    },

    siteConfig: {
        get: () => publicFetch('site-config'),
    },
};

export default publicApi;
